import type {
  BrowserAutomationData,
  BrowserAutomationStorage,
} from '@terra/shared-dto-chat-types';
import { capBrowserAutomationScreenshotBase64 } from '@terra/shared-dto-chat-types';

const MAX_EVALUATE_RESULT_JSON_LENGTH = 8192;
const REDACTED_VALUE = '[redacted]';

function capJsonValue({ value }: { value: unknown }): unknown {
  if (value === undefined) {
    return undefined;
  }

  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    return '[non-serializable result]';
  }

  if (serialized.length <= MAX_EVALUATE_RESULT_JSON_LENGTH) {
    return value;
  }

  return `${serialized.slice(0, MAX_EVALUATE_RESULT_JSON_LENGTH)}…`;
}

function redactStorageValues({
  storage,
}: {
  storage: BrowserAutomationStorage;
}): BrowserAutomationStorage {
  return {
    localStorage: storage.localStorage.map((item) => ({
      key: item.key,
      value: REDACTED_VALUE,
    })),
    sessionStorage: storage.sessionStorage.map((item) => ({
      key: item.key,
      value: REDACTED_VALUE,
    })),
    cookies: storage.cookies.map((item) => ({
      name: item.name,
      value: REDACTED_VALUE,
      httpOnly: item.httpOnly,
    })),
  };
}

export function sanitizeBrowserAutomationResponseData({
  data,
}: {
  data: BrowserAutomationData;
}): BrowserAutomationData {
  const { evaluateResult, storage, screenshotBase64, ...rest } = data;

  return {
    ...rest,
    screenshotBase64: capBrowserAutomationScreenshotBase64(screenshotBase64),
    evaluateResult: capJsonValue({ value: evaluateResult }),
    storage: storage ? redactStorageValues({ storage }) : undefined,
  };
}
